import { Inject, Injectable } from '@angular/core';
import { APPLICATION_NAME_TOKEN, ApplicationName, Audience } from '@ddt-web/config';
import { PortfolioWithWdsTagging, WdsTaggingAttributes, WdsTaggingConfig, WebUserDataAppConfig, WebUserDataConstants } from '@ddt-web/domain';
import { UserDataService, UserProfile } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import { first } from 'rxjs/operators';

import { AudienceService } from './audience.service';

@Injectable({
	providedIn: 'root'
})
export class PortfolioTaggingService {

	private audience: Audience;
	private userProfile: UserProfile;

	constructor(
		private audienceService: AudienceService,
		private userDataService: UserDataService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName
	) {
		this.audienceService.getAudience()
			.pipe(first(audience => !!audience))
			.subscribe(audience => this.audience = audience);

		this.userDataService.getUserProfile()
			.pipe(first(userProfile => !!userProfile))
			.subscribe(userProfile => {
				this.userProfile = userProfile;
			});
	}

	/**
	 *  Adding tags on portfolio creation for analytics purposes
	 *  - origin: Tool (in functional business sense) for which the record has been created for.
	 *  - toolOrigin: App (in terms of code that constructed the object) that created the record.
	 *  - country: Country where the record was created in. Records cannot be shared across different countries.
	 */
	public setAnalyticsTags(data: PortfolioWithWdsTagging, wdsTaggingConfig?: WdsTaggingConfig): PortfolioWithWdsTagging {
		let analyticsAttributes: WdsTaggingAttributes = {};
		const applicationName = get(wdsTaggingConfig, 'applicationNameOverride') || this.applicationName;
		const overrides = get(wdsTaggingConfig, 'overrides');

		const origin = get(overrides, 'origin') || get(WebUserDataAppConfig.APPLICATION_NAMES_PORTFOLIO_SAVING, `${applicationName}.origin`, WebUserDataConstants.APPLICATIONS.BFA_PE);
		const toolOrigin = get(WebUserDataAppConfig.APPLICATION_NAMES_PORTFOLIO_SAVING, `${applicationName}.toolOrigin`, WebUserDataConstants.APPLICATIONS.BFA_PE);
		const defaultApplication = get(WebUserDataAppConfig.APPLICATION_NAMES_PORTFOLIO_SAVING, `${applicationName}.defaultApplication`, WebUserDataConstants.APPLICATIONS.BFA);

		// to set only if new record
		if (get(wdsTaggingConfig, 'isNewRecord')) {
			analyticsAttributes['origin'] = origin;
			analyticsAttributes['toolOrigin'] = get(overrides, 'toolOrigin') || toolOrigin;
		}

		// On edit, array will get merged with existing applications array items
		analyticsAttributes['applications'] = get(overrides, 'applications') || [defaultApplication, origin];
		analyticsAttributes['country'] = get(overrides, 'country') || data['country'] || this.getCountryTagging(applicationName);

		analyticsAttributes = this.checkAladdinU(analyticsAttributes);

		return analyticsAttributes;
	}

	private getCountryTagging(applicationName: string): string {
		const audienceFromCountry = this.audience ? this.audience.country : null;
		return get(WebUserDataAppConfig.APPLICATION_NAMES_PORTFOLIO_SAVING, `${applicationName}.country`) || audienceFromCountry;
	}

	private checkAladdinU(data: WdsTaggingAttributes): WdsTaggingAttributes {
		const aladdinUniversity = get(this.userProfile, 'userAttributes.aladdinUniv');
		if (aladdinUniversity) {
			data['applications'] = [WebUserDataConstants.APPLICATIONS.BFA_ALADDINU];
		}
		return data;
	}
}
